import type { NetworkId } from "@alch/dx-entities";
import { pick } from "lodash-es";
import { parse, stringify } from "qs";
import type { ComposerFormState } from "./form.js";
import { composerFormSchemaForMethod } from "./form.js";
import type { ComposerMethodConfig } from "./methods.js";
import { getUniqueMethodConfig } from "./methods.js";

const serializableProperties = ["network", "method", "query", "body"] as const;

export type SerializableComposerFormState = Pick<
  ComposerFormState,
  (typeof serializableProperties)[number]
>;

export const buildShareUrl = ({
  baseUrl,
  state,
}: {
  baseUrl: string;
  state: SerializableComposerFormState;
}): string => {
  const serializedState = stringify(pick(state, serializableProperties), {
    strictNullHandling: true,
    allowDots: true,
    allowEmptyArrays: true,
  });

  return `${baseUrl}/?${serializedState}`;
};

const booleanParser = (str: string, defaultDecoder: (str: string) => string) =>
  str === "true" ? true : str === "false" ? false : defaultDecoder(str);

export const parseStateFromShareUrl = ({
  methodConfigs,
  search,
}: {
  methodConfigs: ComposerMethodConfig[];
  search: string;
}): {
  state: SerializableComposerFormState;
  methodConfig: ComposerMethodConfig;
} | null => {
  const state = parse(search, {
    allowDots: true,
    ignoreQueryPrefix: true,
    depth: 10,
    strictNullHandling: true,
    allowEmptyArrays: true,
    decoder: booleanParser,
  });

  const methodConfig = getUniqueMethodConfig(methodConfigs, {
    method: String(state.method),
    network: state.network as NetworkId,
  });

  if (methodConfig == null) {
    return null;
  }

  const schema = composerFormSchemaForMethod(methodConfig, {
    strict: false,
  }).partial();

  const parseResult = schema.safeParse(state);

  if (!parseResult.success) {
    return null;
  }

  return {
    state: parseResult.data as SerializableComposerFormState,
    methodConfig,
  };
};
